import { isChinaFacingLexClient } from '@/shared/lib/isChinaFacingLexClient'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { ArtemLogoSymbol } from '@/components/artem-logo-symbol'
import { ChinaPsbFilingFooter } from '@/components/china-psb-filing-footer'
import { TempLogoType } from '@/components/temp-logo-type'
import { AuthInterfaceLanguageSelect } from './auth-interface-language-select'

type AuthLayoutProps = {
  children: React.ReactNode
  /** Product or surface label beside the wordmark (e.g. Lex sign-in). */
  productBadge?: string
  /** Short line under the brand row clarifying the surface. */
  productSubtitle?: string
  /**
   * When true, hides the interface language control (e.g. Lex uses its own session API and
   * English-only bootstrap; avoids calling Lex `/v1/auth/session` from the language select).
   */
  hideInterfaceLanguageSelect?: boolean
}

export function AuthLayout({
  children,
  productBadge,
  productSubtitle,
  hideInterfaceLanguageSelect = false,
}: AuthLayoutProps) {
  const showChinaPsbFiling = isChinaFacingLexClient()

  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      {!hideInterfaceLanguageSelect ? (
        <div
          className={cn(
            'fixed inset-e-4 z-10 sm:inset-e-6',
            showChinaPsbFiling
              ? 'bottom-28 sm:bottom-32'
              : 'bottom-4 sm:bottom-6'
          )}
        >
          <AuthInterfaceLanguageSelect />
        </div>
      ) : null}
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-3'>
            <div className='flex items-center'>
              <ArtemLogoSymbol className='size-10 shrink-0 text-[#02FFD8]' />
              <TempLogoType className='h-4 shrink-0' />
            </div>
            {productBadge ? (
              <Badge
                variant='outline'
                className='font-semibold tracking-wide uppercase'
              >
                {productBadge}
              </Badge>
            ) : null}
          </div>
          {productSubtitle ? (
            <p className='text-sm text-muted-foreground'>{productSubtitle}</p>
          ) : null}
        </div>
        {children}
      </div>
      <ChinaPsbFilingFooter
        layout='inline'
        className='fixed inset-x-0 bottom-0 z-0 bg-background/95 py-2 backdrop-blur-sm'
      />
    </div>
  )
}
